"use client"

import Link from "next/link"
import { type ReactNode } from "react"

interface HeroBannerProps {
  /** Small uppercase eyebrow label (e.g. "Category Guide", "Brand Spotlight") */
  eyebrow: string
  /** Main headline (rendered as h1) */
  title: string
  /** Supporting description paragraph */
  description?: string
  /** Hero image URL — when provided, renders image+text split layout;
   *  when absent, renders text-only gradient layout */
  imageUrl?: string
  /** Alt text for the hero image */
  imageAlt?: string
  /** Brand logo URL — shown above eyebrow in text-only layout (brand variant) */
  logo?: string
  /** Single character fallback when logo URL is absent (e.g. brand initial) */
  logoFallback?: string
  /** CTA button text */
  buttonText?: string
  /** CTA button link — renders a Next.js Link when provided */
  buttonHref?: string
  /** Click handler — used instead of buttonHref for scroll-to-section behavior (guide variant) */
  onButtonClick?: () => void
  /** Custom button icon (defaults to right chevron SVG) */
  buttonIcon?: ReactNode
  /** Additional classes on the outer section */
  className?: string
}

const DEFAULT_CHEVRON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
)

/**
 * HeroBanner — The top-of-page hero section used across all hub page variants.
 *
 * Two layouts:
 * - **Image+text split** (when `imageUrl` is set): lifestyle image on the left,
 *   white panel on the right with eyebrow, title, description, CTA button.
 *   Used in category, feature, and specialty hubs.
 * - **Text-only gradient** (no `imageUrl`): gradient background with optional
 *   logo, eyebrow, title, description, CTA. Used in brand, guide, and comparison hubs.
 *
 * The CTA button renders as a `<Link>` when `buttonHref` is provided, or as a
 * `<button>` when `onButtonClick` is provided (for scroll-to-section behavior).
 */
export function HeroBanner({
  eyebrow,
  title,
  description,
  imageUrl,
  imageAlt,
  logo,
  logoFallback,
  buttonText,
  buttonHref,
  onButtonClick,
  buttonIcon,
  className = "",
}: HeroBannerProps) {
  const icon = buttonIcon ?? DEFAULT_CHEVRON

  const ctaClasses = "inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"

  const ctaButton = buttonText ? (
    buttonHref ? (
      <Link href={buttonHref} className={`${ctaClasses} self-start`}>
        {buttonText}
        {icon}
      </Link>
    ) : onButtonClick ? (
      <button onClick={onButtonClick} className={ctaClasses}>
        {buttonText}
        {icon}
      </button>
    ) : null
  ) : null

  // ── Image + Text split layout ──────────────────────────────────────
  if (imageUrl) {
    return (
      <section className={`mb-12 rounded-2xl overflow-hidden border border-gray-100 shadow-[0_4px_24px_rgba(0,25,69,0.06)] ${className}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[360px]">
          <div className="relative bg-gray-100 min-h-[240px] lg:min-h-full">
            <img
              src={imageUrl}
              alt={(imageAlt || title).endsWith(" | Fridge.com") ? (imageAlt || title) : `${imageAlt || title} | Fridge.com`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/5 to-black/20" />
          </div>
          <div className="flex flex-col justify-center p-8 lg:p-10 bg-white">
            <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-3 block uppercase">
              {eyebrow}
            </span>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-gray-900 mb-3">
              {title}
            </h1>
            {description && (
              <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-md">{description}</p>
            )}
            {ctaButton}
          </div>
        </div>
      </section>
    )
  }

  // ── Text-only gradient layout ──────────────────────────────────────
  return (
    <section className={`mb-12 rounded-2xl overflow-hidden border border-gray-100 bg-gradient-to-br from-white to-gray-50 shadow-[0_4px_24px_rgba(0,25,69,0.06)] ${className}`}>
      <div className="py-12 px-8 lg:px-12">
        {/* Optional brand logo */}
        {logo ? (
          <img src={logo} alt={`${title} logo`} className="h-10 object-contain mb-4" />
        ) : logoFallback ? (
          <div className="w-14 h-14 rounded-xl bg-[#0469f0]/10 flex items-center justify-center mb-4">
            <span className="text-2xl font-black text-[#0469f0]">{logoFallback}</span>
          </div>
        ) : null}
        <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-3 block uppercase">
          {eyebrow}
        </span>
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-gray-900 mb-3">
          {title}
        </h1>
        {description && (
          <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-lg">{description}</p>
        )}
        {ctaButton}
      </div>
    </section>
  )
}
